import DOMPurify from 'dompurify'
import { marked } from 'marked'

/** Opens or closes a fenced code block (``` or ~~~), where indentation is meaningful. */
const CODE_FENCE = /^\s*(```|~~~)/
/** A GFM table row indented far enough (4+ spaces) that Markdown would read it as a code block. */
const INDENTED_TABLE_ROW = /^ {4,}\|/
/** A row Markdown still accepts as part of a table (up to 3 spaces of indentation). */
const TABLE_ROW = /^ {0,3}\|/

/**
 * Left-trims GFM table rows that arrive over-indented.
 *
 * Generated reports (e.g. the accessibility feedback template) emit table rows carrying the
 * template's own indentation. Markdown treats 4+ leading spaces as a code block, so such a table
 * breaks off after its header row and the remaining rows render as a `<pre>` of raw pipes.
 *
 * Only rows that *continue a table already begun on the line above* are dedented, so a genuine
 * indented code block — which Markdown requires a blank line before — keeps its indentation.
 * Fenced blocks are skipped outright.
 */
const dedentTableRows = (md: string): string => {
  let insideFence = false
  let previousIsTableRow = false

  return md
    .split('\n')
    .map(line => {
      if (CODE_FENCE.test(line)) {
        insideFence = !insideFence
        previousIsTableRow = false
        return line
      }
      if (insideFence) return line

      const isTableContinuation = previousIsTableRow && INDENTED_TABLE_ROW.test(line)
      const normalized = isTableContinuation ? line.trimStart() : line
      previousIsTableRow = TABLE_ROW.test(normalized)
      return normalized
    })
    .join('\n')
}

/**
 * Parses a Markdown string and sanitizes the resulting HTML.
 * Use for any user/API-provided Markdown rendered via v-html.
 * `marked.parse` is synchronous by default, so the `string` cast is safe.
 */
export const renderMarkdown = (md: string): string =>
  DOMPurify.sanitize(marked.parse(dedentTableRows(md ?? '')) as string)

/**
 * Reduces an inline Markdown string to its plain text.
 *
 * The support backend tags ticket subjects with Markdown of its own — `**[POSITIVE]** www`,
 * `**[ISSUE]** …` — on both `/support` and `/support/{id}`. The client area shows a subject as a
 * plain string: in an `<h1>`, in a table cell, in a document title. So the syntax is stripped
 * rather than rendered, and the reader sees `[POSITIVE] www`.
 *
 * Going through `marked` and DOMPurify rather than a regex means nested, escaped and malformed
 * syntax all resolve the way an author meant, and any HTML that arrives in the field is reduced
 * to its text too. The result is plain text: interpolate it with `{{ }}`, never with `v-html`.
 */
export const markdownToText = (md: string): string =>
  DOMPurify.sanitize(marked.parseInline(md ?? '') as string, { RETURN_DOM_FRAGMENT: true }).textContent ?? ''

/**
 * Escapes plain text for a slot that renders it with `v-html`.
 *
 * BaseTable draws every cell through `v-html`, so a plain string handed to it is parsed as
 * markup: a subject containing `<` would lose everything after it, and an `<img>` would survive
 * sanitizeHtml's allow-list. Escaping first makes the cell show the text verbatim.
 *
 * Only for the v-html path. Anywhere a value is interpolated with `{{ }}`, Vue escapes it.
 */
export const escapeHtml = (text: string): string => {
  const element = document.createElement('div')
  element.textContent = text ?? ''
  return element.innerHTML
}

/**
 * Sanitizes an HTML string, stripping scripts and dangerous attributes.
 * Use for any API/untrusted HTML rendered via v-html. Accepts the loose
 * value/html types used by table cells and form items (string | number).
 */
export const sanitizeHtml = (html: string | number | null | undefined): string => DOMPurify.sanitize(String(html ?? ''))
